/**
 * Horizon Pro — Instant Cart Drawer Open
 * ─────────────────────────────────────────────────────────────
 * Problem: Horizon's quick-add waits for /cart/add.js + a
 * sections=header re-render (~3.5s, 268KB) BEFORE opening the
 * drawer. That feels broken.
 *
 * Fix: On add-to-cart click, open the drawer immediately with an
 * optimistic "Adding..." state, then let Horizon's normal update
 * flow swap in the real cart contents when the server replies.
 *
 * Safe: only adds an event listener and opens the drawer. Does
 * NOT intercept the fetch, does NOT modify the response, does
 * NOT touch any cart state. Horizon's native logic runs unchanged.
 */

(function () {
  'use strict';
  if (window.__rlInstantOpenInit) return;
  window.__rlInstantOpenInit = true;

  /* Inject the loading-bar CSS once. */
  if (!document.getElementById('hp-cart-loading-bar-style')) {
    const style = document.createElement('style');
    style.id = 'hp-cart-loading-bar-style';
    style.textContent = `
      cart-drawer-component .hp-cart-loading-bar {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 2px;
        background: linear-gradient(90deg, transparent 0%, #C5AB58 50%, transparent 100%);
        background-size: 200% 100%;
        opacity: 0;
        pointer-events: none;
        z-index: 10;
        transition: opacity 0.18s ease;
      }
      cart-drawer-component .hp-cart-loading-bar.is-active {
        opacity: 1;
        animation: hpCartLoadSweep 1.1s ease-in-out infinite;
      }
      @keyframes hpCartLoadSweep {
        0%   { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }
      @media (prefers-reduced-motion: reduce) {
        cart-drawer-component .hp-cart-loading-bar.is-active { animation: none; opacity: 0.6; }
      }
    `;
    document.head.appendChild(style);
  }

  /* Selectors that should trigger an immediate drawer open. */
  const TRIGGER_SELECTORS = [
    '.quick-add__button',                        /* product card hover quick-add */
    'button[name="add"]',                        /* PDP main add-to-cart */
    '.add-to-cart-button',                       /* generic ATC class */
    '[data-hp-upsell-add]',                      /* our cart drawer upsell — already opens */
    'product-form-component button[type="submit"]', /* PDP form submit */
  ].join(',');

  function findDrawer() {
    return document.querySelector('cart-drawer-component');
  }

  function isDrawerAlreadyOpen(drawer) {
    if (!drawer) return false;
    const dlg = drawer.querySelector('dialog');
    return !!(dlg && dlg.open);
  }

  function showLoadingState(drawer) {
    if (!drawer) return;
    /* Mark that we're optimistically open so we know to clean up if
       the add fails. */
    drawer.setAttribute('data-rl-optimistic', '1');
    /* Add a subtle loading indicator to the drawer if one isn't
       already there. Survives the morph because Horizon's morph
       only replaces children, not attributes/data on the host. */
    let bar = drawer.querySelector('.hp-cart-loading-bar');
    if (!bar) {
      bar = document.createElement('div');
      bar.className = 'hp-cart-loading-bar';
      bar.setAttribute('aria-hidden', 'true');
      drawer.appendChild(bar);
    }
    bar.classList.add('is-active');
  }

  function clearLoadingState(drawer) {
    if (!drawer) return;
    drawer.removeAttribute('data-rl-optimistic');
    const bar = drawer.querySelector('.hp-cart-loading-bar');
    if (bar) bar.classList.remove('is-active');
  }

  /* ─── Trigger detection ──────────────────────────────────── */

  document.addEventListener(
    'click',
    function (e) {
      const trigger = e.target && e.target.closest && e.target.closest(TRIGGER_SELECTORS);
      if (!trigger) return;

      /* Skip if disabled or if it's a remove/decrement button. */
      if (trigger.disabled) return;
      const name = (trigger.getAttribute('name') || '').toLowerCase();
      if (name === 'minus' || name === 'plus') return;
      if (trigger.classList.contains('cart-items__remove')) return;

      /* If this is a multi-variant quick-add that opens a modal,
         the modal will handle adding — don't pre-open. The drawer
         will open after the modal's own add. */
      const productUrl = trigger.getAttribute('data-product-url');
      if (productUrl && trigger.getAttribute('data-has-multiple-variants') === 'true') return;

      const drawer = findDrawer();
      if (!drawer || isDrawerAlreadyOpen(drawer)) return;

      /* Open instantly. */
      try {
        if (typeof drawer.showDialog === 'function') {
          drawer.showDialog();
        }
      } catch (_) { /* ignore */ }

      showLoadingState(drawer);
    },
    /* Capture phase so we run BEFORE Horizon's own click handlers
       kick off the fetch. The fetch still runs as usual. */
    true
  );

  /* When Horizon dispatches cart:update (i.e. the add succeeded
     and the morph happened), drop our optimistic state. */
  document.addEventListener('cart:update', function () {
    clearLoadingState(findDrawer());
  });

  /* If the add errors out, also clear our state. */
  document.addEventListener('cart:error', function () {
    clearLoadingState(findDrawer());
  });
})();
